import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, Check, ArrowLeft, BadgeCheck } from "lucide-react";
import api from "@/lib/api";
import { Reveal, FadeIn } from "@/components/Reveal";
import CtaButton from "@/components/CtaButton";
import EnquiryDialog from "@/components/EnquiryDialog";
import EventsStrip from "@/components/EventsStrip";
import Curriculum from "@/components/Curriculum";
import Capstone from "@/components/Capstone";
import ProjectsShowcase from "@/components/ProjectsShowcase";
import BenefitsGrid from "@/components/BenefitsGrid";

export default function CoursePage() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [open, setOpen] = useState(false);
  const [presetInterest, setPresetInterest] = useState("");

  useEffect(() => {
    setCourse(null);
    setNotFound(false);
    api.get(`/courses/${slug}`).then(({ data }) => setCourse(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="w-full mx-auto px-4 md:px-8 lg:px-12 py-28 text-center">
        <p className="font-sans text-xs font-bold text-graphite uppercase tracking-wide">Course not found</p>
        <Link to="/courses" className="font-display font-bold text-3xl mt-4 inline-block text-emerald underline">
          Back to catalog
        </Link>
      </div>
    );
  }
  if (!course) {
    return (
      <div className="w-full mx-auto px-4 md:px-8 lg:px-12 py-28 text-center">
        <p className="font-sans text-xs font-bold text-graphite uppercase tracking-wide">Loading…</p>
      </div>
    );
  }

  const ins = course.instructor;
  const demo = (course.events || []).find((ev) => ev.kind === "demo");
  const openForProject = (interest) => { setPresetInterest(interest || ""); setOpen(true); };

  return (
    <div data-testid="course-page" className="pb-28 lg:pb-0">
      {/* Hero — pale lavender tint, dark text, violet reserved for accents */}
      <section className="mesh-wash relative overflow-hidden">
        <div className="blob-decoration w-[460px] h-[460px] bg-violetlight -top-40 -right-32" />
        <div className="w-full mx-auto px-4 md:px-8 lg:px-12 pt-8 relative">
          <Link
            to="/courses"
            data-testid="course-back"
            className="inline-flex items-center gap-2 font-sans text-xs font-bold text-graphite hover:text-emerald transition-colors"
          >
            <ArrowLeft size={14} /> All courses
          </Link>
        </div>
        <div className="w-full mx-auto px-4 md:px-8 lg:px-12 pt-8 md:pt-10 pb-14 md:pb-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <FadeIn>
                <span className="inline-block rounded-full bg-terracotta text-white px-4 py-1.5 font-sans text-xs font-bold mb-6">
                  {course.category} · {course.overline}
                </span>
              </FadeIn>
              <h1 className="font-display font-bold tracking-tight text-4xl md:text-6xl leading-[1.02] text-ink">
                {course.title}
              </h1>
              <p className="font-sans text-xl md:text-2xl text-ink/80 mt-6 max-w-2xl leading-snug font-medium">
                {course.summary}
              </p>
              <div className="mt-8 flex flex-wrap gap-x-10 gap-y-4 border-t border-line pt-6">
                <Meta label="Format" value={course.format} />
                <Meta label="For" value={course.audience} />
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/3] overflow-hidden rounded-3xl shadow-lg">
                <img src={course.image} alt={course.title} className="h-full w-full object-cover" />
                <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-surface text-emerald font-sans text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
                  <ShieldCheck size={13} /> Verified
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body + rail */}
      <section className="w-full mx-auto px-4 md:px-8 lg:px-12 mt-14 md:mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-8 space-y-16 md:space-y-20">
            <Reveal>
              <div className="space-y-5 max-w-2xl">
                {course.intro.map((p, i) => (
                  <p key={i} className="text-lg md:text-xl leading-relaxed text-ink/90">{p}</p>
                ))}
              </div>
            </Reveal>

            {/* Upcoming — dated batches and demo sessions */}
            {course.events && course.events.length > 0 && (
              <Reveal>
                <SectionLabel>Upcoming</SectionLabel>
                <div className="mt-6">
                  <EventsStrip events={course.events} onEnquire={() => setOpen(true)} testId="course-events" />
                </div>
                <p className="font-sans text-xs font-semibold text-graphite mt-4">
                  No payment is taken on this page. We confirm dates, the fee and any demo
                  payment with you directly.
                </p>
              </Reveal>
            )}

            {/* Instructor — verified card */}
            <Reveal>
              <SectionLabel>The instructor — a verified practitioner</SectionLabel>
              <div data-testid="instructor-card" className="rounded-3xl overflow-hidden bg-surface border border-line mt-6 shadow-md">
                <div className="flex items-center justify-between p-6 border-b border-line bg-emeraldlight">
                  <div className="flex items-center gap-3 text-emerald">
                    <BadgeCheck size={20} />
                    <span className="font-sans text-xs font-bold">Verified track record</span>
                  </div>
                  <span className="font-sans text-xs font-semibold text-graphite">Checked before onboarding</span>
                </div>
                <div className="p-6 md:p-8">
                  <h3 className="font-display font-semibold tracking-tight text-3xl md:text-4xl text-ink">{ins.name}</h3>
                  <p className="font-sans text-xs font-bold text-terracotta mt-3 uppercase tracking-wide">{ins.role}</p>
                  <p className="text-graphite mt-6 leading-relaxed max-w-2xl text-lg">{ins.bio}</p>
                  <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8">
                    {ins.track_record.map((t, i) => (
                      <li key={i} className="flex items-start gap-3 text-[15px] text-ink/85">
                        <Check size={16} className="mt-1 shrink-0 text-emerald" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>

            {/* Benefits — why this program, only for courses that have one */}
            {course.benefits && course.benefits.length > 0 && (
              <Reveal>
                <SectionLabel>Why this program</SectionLabel>
                <div className="mt-6">
                  <BenefitsGrid benefits={course.benefits} testId="course-benefits" />
                </div>
              </Reveal>
            )}

            {/* What you'll learn */}
            <Reveal>
              <SectionLabel>What you'll learn</SectionLabel>
              <div className="mt-6 space-y-3">
                {course.what_you_learn.map((w, i) => (
                  <div key={i} className="flex items-start gap-5 rounded-2xl bg-surface border border-line p-5 shadow-sm">
                    <span className="shrink-0 w-9 h-9 rounded-full bg-emeraldlight text-emerald flex items-center justify-center font-display font-bold text-sm">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-lg leading-snug">{w}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Modules — week-grouped with deliverables when available */}
            <Reveal>
              <SectionLabel>{course.curriculum ? "Your week-by-week journey" : "How it's structured"}</SectionLabel>
              <div className="mt-6">
                {course.curriculum ? (
                  <Curriculum weeks={course.curriculum} testId="course-curriculum" />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {course.modules.map((m, i) => (
                      <div key={i} className="rounded-2xl bg-stone p-6">
                        <span className="font-display font-bold text-4xl text-terracotta block leading-none">{String(i + 1).padStart(2, "0")}</span>
                        <h4 className="font-display font-semibold text-xl mt-4">{m.title}</h4>
                        <p className="text-graphite mt-2 leading-relaxed text-[15px]">{m.desc}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Reveal>

            {/* Capstone — final project journey, only for courses that have one */}
            {course.capstone && (
              <Reveal>
                <SectionLabel>Final capstone</SectionLabel>
                <h3 className="font-display font-semibold text-2xl md:text-3xl text-ink mt-3">{course.capstone.title}</h3>
                <div className="mt-6">
                  <Capstone capstone={course.capstone} testId="course-capstone" />
                </div>
              </Reveal>
            )}

            {/* Projects — portfolio showcase, only for courses that carry real projects */}
            {course.projects && course.projects.length > 0 && (
              <Reveal>
                <SectionLabel>What you'll actually build</SectionLabel>
                <div className="mt-6">
                  <ProjectsShowcase projects={course.projects} testId="course-projects" onEnquire={openForProject} />
                </div>
              </Reveal>
            )}

            {/* Who it's for */}
            <Reveal>
              <SectionLabel>Who it's for</SectionLabel>
              <div className="mt-6 flex flex-wrap gap-3">
                {course.who_its_for.map((w, i) => (
                  <span key={i} className="rounded-full bg-emeraldlight text-emerald px-5 py-2.5 text-[15px] font-bold">{w}</span>
                ))}
              </div>
            </Reveal>

            {/* Testimonials — honest */}
            <Reveal>
              <SectionLabel>From students</SectionLabel>
              <div className="mt-6 rounded-3xl bg-violetlight p-8 md:p-10">
                <p className="font-serif italic text-2xl md:text-3xl leading-snug max-w-2xl text-ink/80">
                  Real reviews from students of this course appear here as batches finish.
                  We only publish genuine quotes from real people — never invented ones.
                </p>
                <p className="font-sans text-xs font-bold text-terracotta mt-6 uppercase tracking-wide">Awaiting first batch</p>
              </div>
            </Reveal>
          </div>

          {/* Right rail — sticky Enquire */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <div className="rounded-3xl overflow-hidden bg-surface border border-line shadow-md">
                <div className="p-6 md:p-8 bg-stone">
                  <p className="font-sans text-xs font-bold text-graphite uppercase tracking-wide">Starting fee</p>
                  <div className="flex items-end gap-2 mt-2">
                    <span className="font-display font-bold tracking-tight text-5xl text-emerald">{course.starting_fee}</span>
                    <span className="font-sans text-xs font-semibold text-graphite mb-2">{course.fee_note}</span>
                  </div>
                  <p className="text-[15px] text-graphite mt-4 leading-relaxed">
                    A starting figure. The exact amount, batch dates and payment terms are
                    confirmed with you directly — no payment is taken here.
                  </p>
                </div>
                <div className="p-6 md:p-8">
                  <CtaButton onEnquire={() => setOpen(true)} full testId="course-enquire-desktop" />
                  <div className="mt-6 space-y-3">
                    <RailPoint icon={<ShieldCheck size={16} />} text="Taught by a verified practitioner" />
                    <RailPoint icon={<Check size={16} />} text="Small batch · in-person" />
                    <RailPoint icon={<Check size={16} />} text="No online payment · a real conversation" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile sticky CTA */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-surface border-t border-line px-4 py-3 shadow-lg"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
      >
        {demo ? (
          <div className="flex items-center gap-2">
            <div className="shrink-0 pr-2">
              <p className="font-sans text-[9px] font-bold text-graphite uppercase tracking-wide">From</p>
              <p className="font-display font-bold text-emerald text-lg leading-none">{course.starting_fee}</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="course-enquire-mobile"
              className="flex-1 rounded-full border border-emerald text-emerald font-sans text-sm font-bold px-3 py-3 text-center"
            >
              Enquire
            </button>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="course-demo-mobile"
              className="flex-1 rounded-full bg-terracotta text-white font-sans text-sm font-bold px-3 py-3 text-center whitespace-nowrap"
            >
              Demo · {demo.fee}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="shrink-0">
              <p className="font-sans text-[10px] font-bold text-graphite uppercase tracking-wide">From</p>
              <p className="font-display font-bold text-emerald text-xl leading-none">{course.starting_fee}</p>
            </div>
            <CtaButton onEnquire={() => setOpen(true)} full size="sm" testId="course-enquire-mobile" />
          </div>
        )}
      </div>

      <EnquiryDialog
        open={open}
        onClose={() => { setOpen(false); setPresetInterest(""); }}
        course={course}
        source="course"
        presetInterest={presetInterest}
      />
    </div>
  );
}

function SectionLabel({ children }) {
  return <p className="font-sans text-xs font-bold text-terracotta uppercase tracking-wide">{children}</p>;
}

function Meta({ label, value }) {
  return (
    <div className="max-w-xs">
      <p className="font-sans text-xs font-bold text-terracotta uppercase tracking-wide">{label}</p>
      <p className="text-base mt-1 text-ink/85">{value}</p>
    </div>
  );
}

function RailPoint({ icon, text }) {
  return (
    <div className="flex items-center gap-3 text-[15px] text-graphite">
      <span className="text-emerald">{icon}</span>
      <span>{text}</span>
    </div>
  );
}
